if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends customElements.get('search-form') {
    static COLUMN_SECTION_ID = 'predictive-search-column';
    static COLUMN_TYPES = ['product', 'article'];

    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector(
        '[data-predictive-search]'
      );
      this.allPredictiveSearchInstances =
        document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.onViewportChange = this.updateResultsMaxHeight.bind(this);

      this.setupEventListeners();
    }

    disconnectedCallback() {
      this.abortController.abort();
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    handleToggleSearchKeywordBar(flag) {
      if (!this.predictiveSearchResults) {
        return null;
      }

      this.predictiveSearchResults.classList.toggle('h-0', flag);
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();

      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector('#predictive-search-results')?.remove();
      }

      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length || this.searchTerm.length === 0) {
        this.handleToggleSearchKeywordBar(true);
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
      this.handleToggleSearchKeywordBar(false);
    }

    onFormSubmit(event) {
      if (
        !this.getQuery().length ||
        this.querySelector('[role="option"][aria-selected="true"]')
      )
        event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector(
        '[role="option"][aria-selected="true"]'
      );

      // Filter out hidden elements (duplicated page and article resources) thanks
      // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
      const allVisibleElements = Array.from(
        this.querySelectorAll('[role="option"]')
      ).filter((element) => element.offsetParent !== null);
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      this.statusElement.textContent = '';

      if (!moveUp && selectedElement) {
        activeElementIndex =
          selectedElementIndex === allVisibleElements.length - 1
            ? 0
            : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex =
          selectedElementIndex === 0
            ? allVisibleElements.length - 1
            : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector(
        '[role="option"][aria-selected="true"]'
      );

      if (selectedOption) selectedOption.click();
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      const params = new URLSearchParams({
        q: searchTerm,
        section_id: 'predictive-search',
        'resources[type]': 'query,collection',
        'resources[limit]': 8,
        'resources[limit_scope]': 'each',
      });

      Promise.all([
        this.fetchSection(
          `${routes.predictive_search_url}?${params}`,
          'predictive-search'
        ),
        ...PredictiveSearch.COLUMN_TYPES.map((type) =>
          this.fetchSection(
            this.getColumnUrl(searchTerm, type),
            PredictiveSearch.COLUMN_SECTION_ID
          )
        ),
      ])
        .then(([resultsSection, ...columnSections]) => {
          const resultsMarkup = this.combineResults(
            resultsSection,
            columnSections
          );
          // Save bandwidth keeping the cache in all instances synced
          this.allPredictiveSearchInstances.forEach(
            (predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            }
          );
          if (searchTerm !== this.searchTerm) return;
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }
          this.close();
          throw error;
        });
    }

    fetchSection(url, sectionId) {
      return fetch(url, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);

          return response.text();
        })
        .then((text) =>
          new DOMParser()
            .parseFromString(text, 'text/html')
            .getElementById(`shopify-section-${sectionId}`)
        );
    }

    getColumnUrl(searchTerm, type) {
      const params = new URLSearchParams({
        q: searchTerm,
        'options[prefix]': 'last',
        type,
        section_id: PredictiveSearch.COLUMN_SECTION_ID,
      });

      return `${this.input.form.getAttribute('action')}?${params}`;
    }

    combineResults(resultsSection, columnSections) {
      columnSections.forEach((columnSection, index) => {
        const type = PredictiveSearch.COLUMN_TYPES[index];
        resultsSection.querySelector(
          `[data-predictive-search-column="${type}"]`
        ).innerHTML = columnSection.innerHTML;
      });

      const groupCounts = [
        ...resultsSection.querySelectorAll('[data-predictive-search-group-count]'),
      ]
        .map((element) => element.textContent.trim())
        .filter(Boolean);

      if (groupCounts.length) {
        resultsSection.querySelector(
          '[data-predictive-search-live-region-count-value]'
        ).textContent = groupCounts.join(', ');
      }

      return resultsSection.innerHTML;
    }

    setLiveRegionLoadingState() {
      this.statusElement =
        this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText =
        this.loadingText || this.getAttribute('data-loading-text');

      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);

      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(
        this.querySelector('[data-predictive-search-live-region-count-value]')
          .textContent
      );
    }

    getResultsMaxHeight() {
      const viewport = window.visualViewport;
      const visibleBottom = viewport
        ? viewport.offsetTop + viewport.height
        : window.innerHeight;
      this.resultsMaxHeight =
        visibleBottom -
        this.predictiveSearchResults.getBoundingClientRect().top;
      return this.resultsMaxHeight;
    }

    updateResultsMaxHeight() {
      if (!this.isOpen) return;
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    }

    open() {
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
      this.togglePredictiveSearchResultHeight();
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
      window.visualViewport?.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('scroll', this.onViewportChange);
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
      this.togglePredictiveSearchResultHeight(false);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.onViewportChange);
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute('style');
      this.togglePredictiveSearchResultHeight(false);
    }

    togglePredictiveSearchResultHeight(force = true) {
      this.predictiveSearchResults.classList.toggle(
        'predictive-search-active',
        force
      );
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
